import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_LINKS, SERVICES_DROPDOWN_LINKS } from '../data/siteMap';

const navLinks = NAV_LINKS.filter((link) => link.path !== '/sitemap' && link.path !== '/services');

const servicesPaths = SERVICES_DROPDOWN_LINKS.map((link) => link.path);

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isServicesActive = servicesPaths.includes(location.pathname);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setDesktopServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!desktopServicesOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setDesktopServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [desktopServicesOpen]);

  const renderNavLink = (
    link: { path: string; label: string },
    options?: { onNavigate?: () => void; mobile?: boolean },
  ) => (
    <Link
      key={link.path}
      to={link.path}
      className={`font-bold transition px-2 rounded ${
        location.pathname === link.path
          ? 'text-brand-green underline underline-offset-4'
          : link.path === '/contact'
            ? options?.mobile
              ? 'px-5 py-2 bg-brand text-white rounded-full shadow hover:bg-brand-green text-center'
              : 'px-4 py-2 bg-brand text-white rounded-full shadow hover:bg-brand-green'
            : 'text-brand hover:text-brand-green'
      } ${options?.mobile ? 'py-2' : 'py-1'}`}
      onClick={options?.onNavigate}
    >
      {link.label}
    </Link>
  );

  const renderServicesDropdown = (variant: 'desktop' | 'mobile') => {
    const isDesktop = variant === 'desktop';

    return (
      <div ref={isDesktop ? servicesRef : undefined} className={isDesktop ? 'relative' : ''}>
        <button
          type="button"
          aria-expanded={isDesktop ? desktopServicesOpen : servicesOpen}
          aria-haspopup="true"
          onClick={() => {
            if (isDesktop) {
              setDesktopServicesOpen((open) => !open);
            } else {
              setServicesOpen((open) => !open);
            }
          }}
          className={`inline-flex items-center gap-1 font-bold transition px-2 py-1 rounded ${
            isServicesActive
              ? 'text-brand-green underline underline-offset-4'
              : 'text-brand hover:text-brand-green'
          } ${!isDesktop ? 'w-full justify-between px-2 py-2' : ''}`}
        >
          Services
          <svg
            className={`h-4 w-4 transition-transform ${(isDesktop ? desktopServicesOpen : servicesOpen) ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {(isDesktop ? desktopServicesOpen : servicesOpen) && (
          <div
            className={
              isDesktop
                ? 'absolute left-0 top-full z-40 mt-2 min-w-[15rem] overflow-hidden rounded-xl border border-gray-100 bg-white py-2 shadow-lg'
                : 'mt-1 flex flex-col gap-1 rounded-xl bg-blue-50/80 p-2'
            }
          >
            {SERVICES_DROPDOWN_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  location.pathname === link.path
                    ? 'bg-brand/10 text-brand-green'
                    : 'text-brand hover:bg-brand/5 hover:text-brand-green'
                }`}
                onClick={() => {
                  setMenuOpen(false);
                  setServicesOpen(false);
                  setDesktopServicesOpen(false);
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md m-0 p-0">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
        <Link to="/" className="flex items-center gap-2">
          <img src={process.env.PUBLIC_URL + '/image/puremedify.png'} alt="Purmedify Logo" className="w-36 h-auto object-contain rounded-2xl" />
        </Link>
        <button className="md:hidden p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand" onClick={() => setMenuOpen(!menuOpen)}>
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <nav className="hidden md:flex gap-4 lg:gap-6 items-center">
          {navLinks.slice(0, 2).map((link) => renderNavLink(link))}
          {renderServicesDropdown('desktop')}
          {navLinks.slice(2).map((link) => renderNavLink(link))}
        </nav>
      </div>
      {menuOpen && (
        <nav className="md:hidden flex flex-col gap-2 px-4 pb-4 bg-white shadow animate-fade-in-down">
          {navLinks.slice(0, 2).map((link) => renderNavLink(link, { mobile: true, onNavigate: () => setMenuOpen(false) }))}
          {renderServicesDropdown('mobile')}
          {navLinks.slice(2).map((link) => renderNavLink(link, { mobile: true, onNavigate: () => setMenuOpen(false) }))}
        </nav>
      )}
    </header>
  );
};

export default Header;
